import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AppUser } from "@/config/appNav";
import { useSession } from "@/session/SessionProvider";
import { useClickOutside } from "@/hooks/useClickOutside";
import NotificationsBell from "./NotificationsBell";
import { cn } from "@/utils/cn";

interface AppTopbarProps {
  title: string;
  spaceLabel: string;
  user: AppUser;
  onOpenSidebar: () => void;
  accountLinks: { label: string; to: string; icon: string }[];
}

export default function AppTopbar({
  title,
  spaceLabel,
  user,
  onOpenSidebar,
  accountLinks,
}: AppTopbarProps) {
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useSession();

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate("/", { replace: true });
  };

  useClickOutside(accountRef, () => setAccountOpen(false), accountOpen);

  const iconButton =
    "flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950 cursor-pointer";

  return (
    <header className="sticky top-0 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 items-center justify-between gap-3 px-4 md:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onOpenSidebar}
            aria-label="Ouvrir le menu"
            className={cn(iconButton, "lg:hidden")}
          >
            <i className="ri-menu-line text-2xl" aria-hidden="true" />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] font-label font-medium uppercase tracking-wide text-foreground-400">
              {spaceLabel}
            </p>
            <h1 className="truncate font-heading text-base font-semibold text-foreground-950 md:text-lg">
              {title}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 md:gap-2">
          <NotificationsBell />

          <div className="relative" ref={accountRef}>
            <button
              type="button"
              onClick={() => {
                setAccountOpen((v) => !v);
              }}
              aria-label="Menu du compte"
              aria-expanded={accountOpen}
              className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-label text-xs font-semibold text-secondary-800">
                {user.initials}
              </span>
              <span className="hidden max-w-[9rem] truncate text-sm font-label font-medium text-foreground-900 sm:block">
                {user.name}
              </span>
              <i className="ri-arrow-down-s-line text-lg text-foreground-500" aria-hidden="true" />
            </button>

            {accountOpen && (
              <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-background-200 bg-background-50 animate-scale-in">
                <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3.5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 font-label text-sm font-semibold text-secondary-800">
                    {user.initials}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-label font-medium text-foreground-950">{user.name}</p>
                    <p className="truncate text-xs text-foreground-500">{user.role}</p>
                  </div>
                </div>
                <div className="p-1.5">
                  {accountLinks.map((link) => (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-label text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
                    >
                      <i className={cn(link.icon, "text-lg text-foreground-500")} aria-hidden="true" />
                      {link.label}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-background-200 p-1.5">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-label text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line text-lg" aria-hidden="true" />
                    Déconnexion
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}